import { PARALLEL_NORMALS_SIN, cramerVelocity, type Vector2 } from "./geometry";
import { MiterSkeletonEdge } from "./MiterSkeletonEdge";
import { MiterSkeletonNode } from "./MiterSkeletonNode";
import type { MiterSourceEdge } from "./MiterSourceEdge";
import type { MiterSourceGraph } from "./MiterSourceGraph";
import type { MiterSourceNode } from "./MiterSourceNode";

// Squared-length threshold for treating a freshly rewired edge as
// already collapsed. Well below any visible edge length, but big
// enough to catch the float noise left behind by simultaneous
// edge-collapse events that our single-pop event loop resolves one
// at a time (e.g. the three inner wavefronts of a triangle all
// reaching the focal point at the same moment).
const COLLAPSE_COINCIDENT_SQ = 1e-12;

// sides.get(n).get(other) yields the two skeleton vertices at
// node n that end the left and right wavefronts of the source
// edge (n, other). Left and right are relative to the outward-
// from-n direction along the edge, so at the b endpoint of an
// (a -> b) source edge they are flipped relative to the global
// a -> b frame — callers reading the b end swap left <-> right.

type SideMap = Map<MiterSourceNode, Map<MiterSourceNode, { left: MiterSkeletonNode; right: MiterSkeletonNode }>>;

function widthOnOutwardSide(sourceEdge: MiterSourceEdge, n: MiterSourceNode, side: "left" | "right"): number {
  if (sourceEdge.a === n) {
    return side === "left" ? sourceEdge.leftWidth : sourceEdge.rightWidth;
  }

  return side === "left" ? sourceEdge.rightWidth : sourceEdge.leftWidth;
}

// Absolute time at which `edge`'s endpoints meet, or null if the
// endpoints are stationary relative to each other or receding (no
// future event — caller should not queue). Derivation: with
// d0 = b - a and Δv = vb - va, the squared length at time t is
// |d0 + (t - t0)·Δv|². Setting its derivative at t = t0 to zero
// and solving for the moment of zero length yields a single
// division. rawRate > 0 is the condition for a future collapse.

function computeCollapseTime(edge: MiterSkeletonEdge, t: number): number | null {
  const pa = edge.a.position;
  const pb = edge.b.position;

  const va = edge.a.velocity;
  const vb = edge.b.velocity;

  const dx = pb.x - pa.x;
  const dy = pb.y - pa.y;

  const dvx = vb.x - va.x;
  const dvy = vb.y - va.y;

  const rawRate = -(dx * dvx + dy * dvy);

  if (rawRate <= 0) {
    return null;
  }

  const lenSq = dx * dx + dy * dy;

  return t + lenSq / rawRate;
}

// Priority queue of scheduled edge collapses, sorted by `time`.
// Stored descending so the next event to fire is always at the
// end and peek/pop are O(1). Insertions use binary search into
// the sorted array (O(log n) compares + O(n) splice); removals
// likewise O(n). At this codebase's scale — a house with tens of
// source edges, yielding hundreds of skeleton edges and events —
// the linear splice cost is trivial, and the code is far simpler
// than a heap with an index map.

interface QueueEntry {
  edge: MiterSkeletonEdge;
  time: number;
}

class EventQueue {
  // Sorted by `time` descending — the imminent event is last.
  private items: QueueEntry[] = [];

  size(): number {
    return this.items.length;
  }

  peek(): QueueEntry | undefined {
    return this.items[this.items.length - 1];
  }

  pop(): QueueEntry | undefined {
    return this.items.pop();
  }

  push(edge: MiterSkeletonEdge, time: number): void {
    // `items` stays sorted descending by time. Binary-search for
    // the lowest index whose existing entry has time <= ours, and
    // splice the new entry in there.
    let lo = 0;
    let hi = this.items.length;

    while (lo < hi) {
      // Floored midpoint of `lo` and `hi`. `>> 1` is an
      // arithmetic right shift, equivalent to Math.floor((lo +
      // hi) / 2) for non-negative integers.
      const mid = (lo + hi) >> 1;
      if (this.items[mid].time > time) {
        lo = mid + 1;
      } else {
        hi = mid;
      }
    }

    this.items.splice(lo, 0, { edge, time });
  }

  remove(edge: MiterSkeletonEdge): void {
    const i = this.items.findIndex((entry) => entry.edge === edge);

    if (i >= 0) {
      this.items.splice(i, 1);
    }
  }
}

export class MiterSkeletonGraph {
  nodes: MiterSkeletonNode[] = [];
  readonly edges: MiterSkeletonEdge[] = [];

  // Per source edge, the two skeleton edges (one per outward side)
  // that carry its wavefront through the run. Populated at seed
  // time and stable thereafter — side edges are modified in place
  // through collapse events (possibly becoming zero-length self-
  // loops, or being repurposed as step chords in an auto-split)
  // but are never replaced or removed, so these references remain
  // the canonical "output edge for this (source edge, side)". The
  // output→input direction is available directly via
  // MiterSkeletonEdge.source, which retains its side back-pointer
  // even after a side edge turns into a degenerate marker.
  readonly sourceSideEdges: Map<MiterSourceEdge, { left: MiterSkeletonEdge; right: MiterSkeletonEdge }> = new Map();

  // Append an edge to the graph and register it on both endpoint
  // incident lists.
  addEdge(edge: MiterSkeletonEdge): void {
    this.edges.push(edge);
    edge.a.incident.push(edge);
    edge.b.incident.push(edge);
  }

  // Build the seeded skeleton at t = 0 from a MiterSourceGraph.
  // Each source edge spawns two wavefront edges (one per side)
  // using its own per-side widths; degree-1 source nodes are
  // closed off by a zero-width cap. Every skeleton vertex is
  // tagged with its per-unit-time velocity from Cramer's rule on
  // the two adjacent wavefront normals and widths.

  static fromSource(source: MiterSourceGraph): MiterSkeletonGraph {
    const graph = new MiterSkeletonGraph();
    const sides: SideMap = new Map();

    // Pass 1: per source node, build wedge vertices and caps.
    for (const n of source.nodes) {
      const neighbors = n.connections;
      const deg = neighbors.length;

      if (deg === 0) {
        continue;
      }

      const entries = neighbors
        .map((other) => {
          const dx = other.position.x - n.position.x;
          const dy = other.position.y - n.position.y;

          const len = Math.hypot(dx, dy);

          const ux = len > 0 ? dx / len : 0;
          const uy = len > 0 ? dy / len : 0;

          return {
            other,
            ux,
            uy,
            angle: Math.atan2(uy, ux),
            sourceEdge: source.findEdge(n, other),
          };
        })
        .sort((p, q) => p.angle - q.angle);

      const atN: Map<MiterSourceNode, { left: MiterSkeletonNode; right: MiterSkeletonNode }> = new Map();

      sides.set(n, atN);

      if (deg === 1) {
        const e = entries[0];

        if (!e.sourceEdge) {
          continue;
        }

        // Outward-from-n direction along the source edge.
        const ux = e.ux;
        const uy = e.uy;

        const nL: Vector2 = { x: -uy, y: +ux };
        const nR: Vector2 = { x: +uy, y: -ux };

        const capN: Vector2 = { x: -ux, y: -uy };

        const leftW = widthOnOutwardSide(e.sourceEdge, n, "left");
        const rightW = widthOnOutwardSide(e.sourceEdge, n, "right");

        const leftV = new MiterSkeletonNode(n.position);
        const rightV = new MiterSkeletonNode(n.position);

        // Zero-width cap: Cramer yields a velocity orthogonal to
        // the cap normal with magnitude equal to the side's
        // width — i.e., exactly l * nL and l * nR.

        const vL = cramerVelocity(capN, 0, nL, leftW) ?? { x: 0, y: 0 };
        const vR = cramerVelocity(capN, 0, nR, rightW) ?? { x: 0, y: 0 };

        leftV.velocity = vL;
        rightV.velocity = vR;

        graph.nodes.push(leftV, rightV);

        const cap = new MiterSkeletonEdge(leftV, rightV, capN, 0, {
          kind: "cap",
          sourceNode: n,
        });

        graph.addEdge(cap);

        atN.set(e.other, { left: leftV, right: rightV });

        continue;
      }

      // degree >= 2: build one wedge per consecutive CCW pair.
      // Untagged wedge: a single vertex on both adjacent
      // wavefronts with a Cramer-blended velocity. Tagged wedge:
      // two vertices each bound to just one adjacent wavefront,
      // plus a zero-motion chord between them.

      const wedges: {
        ccwFirst: MiterSkeletonNode; // used as "left of entries[i]"
        ccwLast: MiterSkeletonNode; // used as "right of entries[(i+1) % deg]"
      }[] = new Array(deg);

      for (let i = 0; i < deg; i++) {
        const ei = entries[i];
        const ej = entries[(i + 1) % deg];

        const nL: Vector2 = { x: -ei.uy, y: +ei.ux };
        const nR: Vector2 = { x: +ej.uy, y: -ej.ux };

        const la = ei.sourceEdge ? widthOnOutwardSide(ei.sourceEdge, n, "left") : 0;
        const lb = ej.sourceEdge ? widthOnOutwardSide(ej.sourceEdge, n, "right") : 0;

        const tagged = !!ei.sourceEdge && source.hasWedgeTag(n, ei.sourceEdge);

        // Auto-split: when the two flanking wavefronts are nearly
        // parallel (within 1°) and their widths differ, the
        // geometry is inconsistent as a single wedge vertex — the
        // two wavefronts are parallel lines at different offsets
        // that never meet. Build the same chord-split as an
        // explicit tag would, so the step that the geometry
        // demands actually appears.
        const wedgeCross = nL.x * nR.y - nL.y * nR.x;
        const inconsistent =
          !!ei.sourceEdge && !!ej.sourceEdge && Math.abs(wedgeCross) < PARALLEL_NORMALS_SIN && la !== lb;

        const shouldSplit = (tagged && !!ei.sourceEdge) || inconsistent;

        if (shouldSplit) {
          // Chord normal perpendicular to the wedge bisector.
          // See "Step-tagged wedges" in skeleton.html: using the
          // two wavefront normals as A and B, atan2 returns a
          // signed angle in [-π, π), which exactly covers every
          // possible wedge orientation — no wrap-around.

          const dot = nL.x * nR.x + nL.y * nR.y;
          const phi = Math.atan2(wedgeCross, dot);
          const c = Math.cos(phi / 2);
          const s = Math.sin(phi / 2);

          // Bisector h = rotate nL by phi/2.
          const h: Vector2 = {
            x: nL.x * c - nL.y * s,
            y: nL.x * s + nL.y * c,
          };

          // Chord normal m = h rotated by +90° (either sign of
          // rotation works since the width-0 constraint m·v = 0
          // is invariant to it).
          const m: Vector2 = { x: -h.y, y: h.x };

          const vA = new MiterSkeletonNode(n.position);
          const vB = new MiterSkeletonNode(n.position);

          // Each split vertex's velocity falls out of the
          // ordinary Cramer solve with (m, 0) on one constraint
          // and (side normal, side width) on the other.

          const velA = cramerVelocity(m, 0, nL, la);
          const velB = cramerVelocity(m, 0, nR, lb);

          if (velA) {
            vA.velocity = velA;
          }

          if (velB) {
            vB.velocity = velB;
          }

          graph.nodes.push(vA, vB);

          const chord = new MiterSkeletonEdge(vA, vB, m, 0, { kind: "chord" });
          graph.addEdge(chord);

          wedges[i] = { ccwFirst: vA, ccwLast: vB };
        } else {
          const v = new MiterSkeletonNode(n.position);

          if (ei.sourceEdge && ej.sourceEdge) {
            const vel = cramerVelocity(nL, la, nR, lb);

            if (vel) {
              v.velocity = vel;
            }
          }

          graph.nodes.push(v);

          wedges[i] = { ccwFirst: v, ccwLast: v };
        }
      }

      // For each incident edge i, the wedge on its outward-left
      // is the one going CCW from it — wedges[i], entering via
      // its ccwFirst side. The wedge on its outward-right is the
      // one going CW from it — wedges[i-1 mod deg], entering via
      // its ccwLast side.
      for (let i = 0; i < deg; i++) {
        atN.set(entries[i].other, {
          left: wedges[i].ccwFirst,
          right: wedges[(i + deg - 1) % deg].ccwLast,
        });
      }
    }

    // Pass 2: per source edge (stored as a -> b), wire up the
    // two side wavefronts. Sides recorded at a are already in
    // the outward-from-a frame, which coincides with the global
    // a -> b orientation. At b the frame is reversed, so
    // outward-from-b left = global right and vice versa.

    for (const edge of source.edges) {
      const a = edge.a;
      const b = edge.b;

      const dx = b.position.x - a.position.x;
      const dy = b.position.y - a.position.y;

      const len = Math.hypot(dx, dy);

      if (len === 0) {
        continue;
      }

      const ux = dx / len;
      const uy = dy / len;

      const nL: Vector2 = { x: -uy, y: +ux };
      const nR: Vector2 = { x: +uy, y: -ux };

      const sa = sides.get(a)?.get(b);
      const sb = sides.get(b)?.get(a);

      if (!sa || !sb) {
        continue;
      }

      const leftAtA = sa.left;
      const leftAtB = sb.right;

      const rightAtA = sa.right;
      const rightAtB = sb.left;

      const leftEdge = new MiterSkeletonEdge(leftAtA, leftAtB, nL, edge.leftWidth, {
        kind: "side",
        sourceEdge: edge,
        side: "left",
      });

      graph.addEdge(leftEdge);

      const rightEdge = new MiterSkeletonEdge(rightAtA, rightAtB, nR, edge.rightWidth, {
        kind: "side",
        sourceEdge: edge,
        side: "right",
      });

      graph.addEdge(rightEdge);

      graph.sourceSideEdges.set(edge, { left: leftEdge, right: rightEdge });
    }

    return graph;
  }

  // Advance every vertex by velocity * dt in place. No event
  // detection, no topology changes.

  step(dt: number): void {
    for (const node of this.nodes) {
      node.position.x += node.velocity.x * dt;
      node.position.y += node.velocity.y * dt;
    }
  }

  // Run the wavefront forward from t = 0 to t = T = 1, processing
  // edge-collapse events in order. Topology changes at each event:
  // the collapsed edge's two endpoints merge into a new vertex,
  // the two edges that were incident to them (other than the
  // collapsed one) are rewired to the new vertex, and the new
  // vertex's velocity is re-solved from those two wavefronts.
  // After the last event within the run we advance the rest of
  // the way to T.

  runToEnd(): void {
    const T = 1;
    let t = 0;

    const queue = new EventQueue();

    // Seed: insert every edge with a valid future collapse.
    for (const edge of this.edges) {
      const time = computeCollapseTime(edge, 0);

      if (time !== null) {
        queue.push(edge, time);
      }
    }

    while (queue.size() > 0) {
      const head = queue.peek() as QueueEntry;
      const tAbs = head.time;

      if (tAbs >= T) {
        break;
      }

      const BC = (queue.pop() as QueueEntry).edge;
      const elapsed = tAbs - t;

      if (elapsed > 0) {
        this.step(elapsed);
      }

      t = tAbs;

      this.processEvent(BC, queue, t);
    }

    // Advance the rest of the way to T.
    const remaining = T - t;

    if (remaining > 0) {
      this.step(remaining);
    }
  }

  // Process a single collapse event. The collapsed edge BC is
  // not removed from the graph; instead every edge incident to
  // BC's endpoints migrates to a new merge vertex P, which turns
  // BC into a zero-length self-loop at P. That self-loop is kept
  // alive for the rest of the run as a degenerate marker: its
  // source back-pointer is the only surviving record that the
  // underlying source edge existed on this side of the skeleton,
  // so preserving it makes the output→input mapping total over
  // every (source edge, side) pair rather than partial. Any pre-
  // existing self-loops inherited from earlier events, plus a
  // bigon partner of BC that also collapses during the migration,
  // ride along the same way.
  //
  // When AB and CD (the surviving non-self-loop neighbors at B
  // and C) are near-parallel with differing widths, a single
  // vertex P is geometrically inconsistent. P is replaced by two
  // vertices vA and vB that drift apart, with BC itself re-
  // purposed as the zero-width step chord between them — same
  // edge object, just with its geometry overwritten to the
  // chord's. BC's "side" source back-pointer survives the
  // repurposing, so the chord is the persistent map entry for
  // that source edge instead of a separate dedicated chord edge
  // being created. Other edges still anchored to P after the
  // split (previously-inherited self-loops, or a bigon partner
  // that collapsed alongside BC) have no natural home on either
  // side; they get parked on vA arbitrarily.
  //
  // Coincident-endpoint edges that fall out of the migration get
  // pushed back onto the queue scheduled at the current time so
  // they pop next without advancing it. A genuine collapse-time
  // recomputation on a near-degenerate edge would land
  // unpredictably, so we bypass that computation for these.

  private processEvent(BC: MiterSkeletonEdge, queue: EventQueue, t: number): void {
    const B = BC.a;
    const C = BC.b;

    const AB = B.otherIncident(BC);
    const CD = C.otherIncident(BC);

    const P = new MiterSkeletonNode(B.position);

    // Migrate every edge incident to B or C over to P in one
    // pass. This covers BC (both endpoints at B and C → both at
    // P, i.e. a self-loop), any bigon partner of BC (same fate),
    // any self-loops already at B or C from prior events (stay
    // self-loops, now at P), and AB/CD (one endpoint at B or C →
    // now at P, the other unchanged). Self-loops are listed
    // twice in P.incident to match the convention that addEdge
    // uses for a new self-loop edge.

    const toMigrate = new Set<MiterSkeletonEdge>();

    for (const e of B.incident) {
      toMigrate.add(e);
    }

    for (const e of C.incident) {
      toMigrate.add(e);
    }

    for (const e of toMigrate) {
      if (e.a === B || e.a === C) {
        e.a = P;
      }

      if (e.b === B || e.b === C) {
        e.b = P;
      }
    }

    for (const e of toMigrate) {
      P.incident.push(e);

      if (e.a === P && e.b === P) {
        P.incident.push(e);
      }
    }

    this.nodes = this.nodes.filter((node) => node !== B && node !== C);
    this.nodes.push(P);

    queue.remove(BC);

    if (AB && CD && AB !== CD) {
      const D = AB.normal.x * CD.normal.y - AB.normal.y * CD.normal.x;
      const nearParallel = Math.abs(D) < PARALLEL_NORMALS_SIN;
      const widthsDiffer = AB.width !== CD.width;

      if (nearParallel && widthsDiffer) {
        const dot = AB.normal.x * CD.normal.x + AB.normal.y * CD.normal.y;
        const phi = Math.atan2(D, dot);

        const c = Math.cos(phi / 2);
        const s = Math.sin(phi / 2);

        const h: Vector2 = {
          x: AB.normal.x * c - AB.normal.y * s,
          y: AB.normal.x * s + AB.normal.y * c,
        };

        const m: Vector2 = { x: -h.y, y: h.x };

        const vA = new MiterSkeletonNode(P.position);
        const vB = new MiterSkeletonNode(P.position);

        const velA = cramerVelocity(m, 0, AB.normal, AB.width);
        const velB = cramerVelocity(m, 0, CD.normal, CD.width);

        if (velA) {
          vA.velocity = velA;
        }

        if (velB) {
          vB.velocity = velB;
        }

        // Reassign AB's P endpoint to vA, CD's P endpoint to vB.
        if (AB.a === P) {
          AB.a = vA;
        } else {
          AB.b = vA;
        }

        if (CD.a === P) {
          CD.a = vB;
        } else {
          CD.b = vB;
        }

        vA.incident.push(AB);
        vB.incident.push(CD);

        // Repurpose the just-collapsed BC as the step chord
        // between vA and vB. BC keeps its "side" source back-
        // pointer, so the chord doubles as the map entry for BC's
        // source edge; no separate chord edge is created.
        BC.a = vA;
        BC.b = vB;
        BC.normal = m;
        BC.width = 0;

        vA.incident.push(BC);
        vB.incident.push(BC);

        // Everything else still anchored at P after moving AB,
        // CD, and BC away is a stranded self-loop (previously
        // inherited, or a bigon partner that collapsed alongside
        // BC during migration). Park them on vA; their role is
        // purely as mapping markers, so the choice is cosmetic.
        const orphans = new Set<MiterSkeletonEdge>();

        for (const e of P.incident) {
          if (e === AB || e === CD || e === BC) {
            continue;
          }

          if (e.a === P && e.b === P) {
            orphans.add(e);
          }
        }

        for (const e of orphans) {
          e.a = vA;
          e.b = vA;
          vA.incident.push(e, e);
        }

        this.nodes = this.nodes.filter((node) => node !== P);
        this.nodes.push(vA, vB);
      } else {
        const v = cramerVelocity(AB.normal, AB.width, CD.normal, CD.width);

        if (v) {
          P.velocity = v;
        } else {
          P.velocity = { x: 0, y: 0 };
        }
      }
    } else {
      P.velocity = { x: 0, y: 0 };
    }

    // Inspect the new edges. AB === CD gets processed once.
    const newEdges: MiterSkeletonEdge[] = [];

    if (AB) {
      newEdges.push(AB);
    }

    if (CD && CD !== AB) {
      newEdges.push(CD);
    }

    for (const newEdge of newEdges) {
      queue.remove(newEdge);

      if (newEdge.a === newEdge.b) {
        // Bigon partner that collapsed alongside BC into a
        // self-loop during migration. Preserved as a mapping
        // marker; no future event to schedule.
        continue;
      }

      const dx = newEdge.a.position.x - newEdge.b.position.x;
      const dy = newEdge.a.position.y - newEdge.b.position.y;

      if (dx * dx + dy * dy < COLLAPSE_COINCIDENT_SQ) {
        // Endpoints are coincident (within float noise) — fire
        // this edge at the current time, ahead of anything else.
        // Scheduling it at t puts it at the end of the queue
        // without advancing time on the next pop.
        queue.push(newEdge, t);
        continue;
      }

      const time = computeCollapseTime(newEdge, t);

      if (time !== null) {
        queue.push(newEdge, time);
      }
    }
  }
}
